import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { deleteAllChats, deleteUserAccount, updateUserName, getUserInfo } from "../../../api";
import OGFleetIcon from '../../../../assets/OGFLEET Logo.png';
import UpgradePlan from "../../upgradePlan/UpgradePlan";
import {
  PanelRightOpen,
  SquarePen,
  Ellipsis,
  Pen,
  Trash2,
  Settings,
  CircleUserRound,
  LogOut,
  Edit,
  X,
  Zap,
  Crown
} from "lucide-react"; // Added Zap and Crown icons for the upgrade button

const Sidebar = ({
  isOpen,
  toggleSidebar,
  startNewChat,
  chatHistory,
  handleHistoryClick,
  currentConversationIndex,
  renameChat,
  deleteChat,
  setCurrentConversationIndex,
  refreshChats,
  setGlobalUserName,
  setGlobalEmail,
  isDarkTheme
}) => {
  const [showOptionsIndex, setShowOptionsIndex] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameInput, setRenameInput] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userName, setUserName] = useState("-");
  const [userEmail, setUserEmail] = useState("-");
  const [isEditingName, setIsEditingName] = useState(false); // New state for editing name
  const optionsRef = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptionsIndex(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleShowOptions = (index) => {
    setShowOptionsIndex(index === showOptionsIndex ? null : index);
  };

  const handleRenameClick = (index) => {
    setIsRenaming(true);
    setRenameInput(chatHistory[index].title);
    setShowOptionsIndex(null);
  };

  const handleRenameSubmit = async (index) => {
    const chatId = chatHistory[index].id;
    try {
      await renameChat(chatId, renameInput);
    } catch (error) {
      console.error("Error renaming chat:", error);
    } finally {
      setIsRenaming(false);
    }
  };

  const confirmDeleteChat = (index) => {
    setChatToDelete(index);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const chatId = chatHistory[chatToDelete].id;
    try {
      await deleteChat(chatId);
    } catch (error) {
      console.error("Error deleting chat:", error);
    } finally {
      setChatToDelete(null);
      setIsModalOpen(false);
      setShowOptionsIndex(null);
    }
  };

  const handleDeleteAllChats = async () => {
    if (window.confirm("Are you sure you want to delete all chats? This action cannot be undone.")) {
      try {
        const res = await deleteAllChats();
        refreshChats();
        console.log("All chats deleted successfully!");
      } catch (error) {
        console.log("Error deleting chats!");
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const handleEditName = async () => {
    const newName = prompt("Enter your new name:", userName);
    setIsEditingName(true);
    if (newName && newName !== userName) {
      try {
        const res = await updateUserName(newName);
        console.log("User name updated successfully!");
        setUserName(newName);
      } catch (error) {
        console.log("Error updating user name!");
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      try {
        const res = await deleteUserAccount();
        console.log("Account deleted successfully!");
        logout();
        navigate("/auth");
      } catch (error) {
        console.log("Error deleting account!");
      }
    }
  };

  const setUserInfo = async () => {
    try {
      const data = await getUserInfo();
      setUserName(data.user_name);
      setUserEmail(data.user_email);
      setGlobalUserName(data.user_name);
      setGlobalEmail(data.user_email);
    } catch (error) {
      console.error("Failed to fetch user info:", error);
    }
  }

  const handleLogoClick = () => {
    navigate("/");
  };

  useEffect(() => {
    setUserInfo();
  }, []);

  return (
    <>
      <div
        className={`w-[250px] ${isDarkTheme ? 'bg-dark-background text-white border-dark-border' : 'bg-[#fffcfc] text-gray-800 border-gray-200'} flex flex-col justify-between transition-transform duration-[0.4s] ease-in-out fixed h-full z-[200] border-r shadow-sm ${isOpen ? "transform translate-x-0" : "transform -translate-x-full"
          }`}
      >
        {/* Logo and Controls Section */}
        <div className="flex flex-col">
          {/* Company Logo/Title */}
          {isOpen && (
            <div className="py-0 px-3 flex justify-start items-center"
              >
              <img
                src={OGFleetIcon}
                alt="OGFLEET Icon"
                className="w-[100px] h-[90px] ml-1 cursor-pointer"
                onClick={handleLogoClick}
              />

              <h1 className="text-2xl font-bold -ml-7 cursor-pointer" onClick={handleLogoClick}>
                <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">OG</span>
                <span className={isDarkTheme ? "text-white" : "text-gray-800"}>-FLEET</span>
              </h1>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center p-[0px_10px_10px_15px] justify-between">
            <div className="relative group">
              <PanelRightOpen
                className={`w-[22px] h-[22px] cursor-pointer mr-[15px] ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}
                onClick={toggleSidebar}
              />
              <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 hidden group-hover:block ${isDarkTheme ? 'text-white bg-gray-800' : 'text-gray-800 bg-white'} p-2 rounded text-xs shadow-md border ${isDarkTheme ? 'border-gray-700' : 'border-gray-200'}`}>
                Close Sidebar
              </div>
            </div>

            <div className="relative group">
              <SquarePen
                className={`w-[22px] h-[22px] cursor-pointer mr-[15px] ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}
                onClick={startNewChat}
              />
              <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 hidden group-hover:block  ${isDarkTheme ? 'text-white bg-gray-800' : 'text-gray-800 bg-white'} p-2 rounded text-xs shadow-md border ${isDarkTheme ? 'border-gray-700' : 'border-gray-200'}`}>
                New Chat
              </div>
            </div>
          </div>
        </div>

        {/* History Section */}
        <div className="history flex-1 overflow-y-auto p-[15px] mt-1 mb-3">
          <h3 className={`text-[1rem] font-bold mb-[10px] ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>History</h3>
          {chatHistory.map((chat, index) => (
            <div
              key={chat.id}
              className={`relative p-[8px_10px] rounded-[8px] cursor-pointer transition-colors duration-[0.3s] flex justify-between items-center mb-1 ${currentConversationIndex === index
                ? isDarkTheme ? "bg-gray-700" : "bg-gray-200"
                : isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              onClick={() => handleHistoryClick(index)}
            >
              {isRenaming && currentConversationIndex === index ? (
                <input
                  type="text"
                  value={renameInput}
                  onChange={(e) => setRenameInput(e.target.value)}
                  onBlur={() => handleRenameSubmit(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRenameSubmit(index);
                    if (e.key === "Escape") setIsRenaming(false);
                  }}
                  autoFocus
                  className={`${isDarkTheme ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-800 border-gray-300'} p-[8px] rounded-[8px] w-full border`}
                />
              ) : (
                <span className={`${isDarkTheme ? 'text-gray-300' : 'text-gray-700'} text-sm`}>{chat.title}</span>
              )}
              <Ellipsis
                className="w-[16px] h-[16px] cursor-pointer ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowOptions(index);
                }}
              />
              {showOptionsIndex === index && (
                <div
                  className={`absolute top-0 right-0 ${isDarkTheme ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl w-[130px] shadow-md z-[10] max-h-[150px] overflow-y-auto justify-center items-center p-2 border`}
                  ref={optionsRef}
                >
                  <div
                    className={`flex items-center p-[6px_4px] ${isDarkTheme ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} cursor-pointer 
                    rounded-[4px]`}
                    onClick={() => handleRenameClick(index)}
                  >
                    <Pen className="w-[16px] h-[16px] mr-[8px]" />
                    Rename
                  </div>
                  <div
                    className={`flex items-center p-[6px_4px] text-red-500 cursor-pointer ${isDarkTheme ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-[4px]`}
                    onClick={() => confirmDeleteChat(index)}
                  >
                    <Trash2 className="w-[16px] h-[16px] mr-[8px]" />
                    Delete
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Upgrade Plan Button */}
        <div className="p-[10px_15px] mt-auto mb-4">
          <button
            className={`${isDarkTheme ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' : 'bg-black text-white hover:bg-white hover:text-black border-gray-300'} p-[10px] m-0 cursor-pointer rounded-[8px] flex items-center justify-center transition-colors duration-[0.3s] w-full border shadow-sm`}
            onClick={() => navigate('/upgrade')}
          >
            <Crown className="inline w-[18px] h-[18px] mr-[8px] text-[#ffb42c]" />
            <span className="font-medium ">Upgrade Plan</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[300] animate-fade-in">
          <div className={`
      ${isDarkTheme
              ? 'bg-gray-800 border-gray-700 text-gray-100'
              : 'bg-white border-gray-200 text-gray-800'
            }
      p-6 rounded-xl shadow-2xl w-[320px] border
      animate-scale-in
    `}>
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`p-3 rounded-full ${isDarkTheme ? 'bg-red-900/30' : 'bg-red-100'} `}>
                <svg
                  className={`w-6 h-6 ${isDarkTheme ? 'text-red-400' : 'text-red-600'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Text Content */}
            <h3 className="text-center text-lg font-semibold mb-2">
              Delete Chat
            </h3>
            <p className="text-center text-sm mb-6 text-gray-500">
              Are you sure you want to permanently delete this chat?
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className={`
            flex-1 py-1 rounded-lg font-medium transition-all
            ${isDarkTheme
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200'
                  }
          `}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className={`
            flex-1 py-1 rounded-lg font-medium transition-all
            ${isDarkTheme
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-red-600 hover:bg-red-700'
                  }
            text-white shadow-md hover:shadow-lg
          `}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Sidebar;